/**
 * Display-only number rendering for the pricing page.
 *
 * ⚠️ Dates NEVER pass through here. They go to `utils/dateFormatter.js`, which is the
 * one place that knows the business timezone (CLAUDE.md). This file handles quantities
 * only — money and percentages.
 *
 * Digits: Latin, deliberately. `numberFormatter.formatCount` renders Arabic-Indic per
 * the design system's `ar-SA` locale standard, and that is right for counts — but every
 * OTHER money surface in the app (`/finance`, the booking modal, payments) shows Latin
 * digits with `ر.س`, and ops compares this page's achieved rate against `/finance`'s ADR
 * directly. Two digit systems for the same quantity across two screens is a worse cost
 * than the inconsistency with the counts convention. Counts and any `x of y` pair on
 * this page avoid a slash entirely and read as words, so the bidi problem
 * `formatProgress` exists to solve never arises here (I18N_STRATEGY.md:204).
 */

const NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

// Money inside a COLUMN, where the figures are read against each other and must line up.
// The natural formatter drops trailing zeros, so a column reads 45 / 27 / 102.9 / 107.99
// and the decimal points wander. Headline tiles keep the natural form — "300.00 ر.س" as a
// standalone number is noise.
const NUMBER_EXACT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/**
 * A money amount, or null when there is nothing to show.
 * Returns null rather than '0' so callers must decide what absence looks like —
 * an unpriced night is unknown, never free.
 */
export const money = (value) =>
  (typeof value === 'number' && Number.isFinite(value) ? NUMBER.format(value) : null);

/** A money amount with both decimals always shown — for figures stacked in a column. */
export const moneyExact = (value) =>
  (typeof value === 'number' && Number.isFinite(value) ? NUMBER_EXACT.format(value) : null);

/** A whole-number percentage. Fractions of a percent are noise at this sample size. */
export const percent = (value) =>
  (typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : null);

/**
 * A percentage as set, up to two decimals — for a configured discount, where 15.39% is a real
 * setting (P-0010 weekly) and rounding it to 15% would show two different settings as one.
 */
export const percentExact = (value) =>
  (typeof value === 'number' && Number.isFinite(value) ? `${NUMBER.format(value)}%` : null);

/** A plain count. */
export const count = (value) =>
  (typeof value === 'number' && Number.isFinite(value) ? NUMBER.format(value) : null);
